import { Box, BoxProps } from '@mui/material';
import clsx from 'clsx';
import { isValidElement, ReactNode, useId, useMemo } from 'react';

import { TypographyProps } from '../../Typography';

import {
  ControlLabelContext,
  ControlLabelSettings,
} from '../../../components/controls/ControlLabelContext';
import { InfoTooltipProps } from '../../../components/tooltip/InfoTooltip';
import { WithRef } from '../../../types';
import { mergeSx } from '../../../utils';

import {
  LabelWithDescription,
  LabelWithDescriptionProps,
  LabelWithDescriptionSlotProps,
} from '../LabelWithDescription';
import { labelSizeToLineHeight } from '../Label';

import { controlLabelClassNames } from './classNames';

export type ControlLabelSlotProps = {
  label?: Partial<TypographyProps>;
  infoTooltip?: Partial<InfoTooltipProps>;
  descriptionWrapper?: Omit<Partial<TypographyProps>, 'variant'>;
  bottomContentWrapper?: Partial<BoxProps>;
  errorMessageWrapper?: LabelWithDescriptionSlotProps['errorMessageWrapper'];
};

export type ControlLabelProps = Omit<LabelWithDescriptionProps, 'slotProps'> & {
  /**
   * The control to label. Clicking the label text activates it via a native
   * `<label for>`. DS controls (Checkbox, Radio, Switch) receive the generated
   * id through ControlLabelContext; a control that carries its own `id` keeps
   * it and becomes the association target.
   */
  control: ReactNode;
  /** Per-slot props for label, infoTooltip, descriptionWrapper, bottomContentWrapper, and errorMessageWrapper */
  slotProps?: ControlLabelSlotProps;
};

/**
 * Pairs a control with its label, description, info tooltip, error message and
 * bottom content. The label text is associated with the control via a native
 * `htmlFor` link (the id is generated unless the control carries its own), so
 * clicking the text activates the control while the info icon, description,
 * error and bottom content stay outside the click target.
 */
export const ControlLabel = ({
  ref,
  control,
  label,
  description,
  labelWeight,
  required,
  infoTooltip,
  bottomContent,
  error: errorProp,
  size = 'sm',
  disabled: disabledProp,
  slotProps,
  className,
  sx,
  testId,
  ...labelWithDescriptionProps
}: WithRef<HTMLDivElement, ControlLabelProps>) => {
  const hasError = !!errorProp;
  const hasErrorMessage = !!errorProp && typeof errorProp !== 'boolean';

  // The label points AT the control by id. A control that carries its own
  // explicit id keeps it (and becomes the `for` target); otherwise a
  // generated id is broadcast via ControlLabelContext so opted-in DS controls
  // apply it to their native input.
  const generatedId = useId();
  const controlOwnId = isValidElement(control)
    ? (() => {
        const id = (control.props as { id?: unknown }).id;
        return typeof id === 'string' ? id : undefined;
      })()
    : undefined;
  const controlId = controlOwnId ?? generatedId;

  // Broadcast the label's own settings via ControlLabelContext (the only
  // channel), so the wrapped control can read them.
  const contextValue = useMemo<ControlLabelSettings>(
    () => ({
      id: controlId,
      size,
      required,
      disabled: disabledProp,
      error: hasError || undefined,
    }),
    [controlId, size, required, disabledProp, hasError]
  );

  return (
    <ControlLabelContext.Provider value={contextValue}>
      <Box
        ref={ref}
        data-testid={testId}
        className={clsx(
          controlLabelClassNames.root,
          disabledProp && controlLabelClassNames.disabled,
          required && controlLabelClassNames.required,
          hasError && controlLabelClassNames.error,
          className
        )}
        sx={mergeSx(
          {
            display: 'grid',
            // Control cell | 8px | text block. The text block owns the label,
            // description, error and bottom rows; the control is pinned to the
            // FIRST label line via a cell height equal to the label line-height.
            gridTemplateColumns: 'max-content 8px minmax(0, 1fr)',
            alignItems: 'flex-start',
          },
          sx
        )}
      >
        {/* Pin the cell to the FIRST label line (flex-start + line-height). */}
        <Box
          className={controlLabelClassNames.control}
          sx={{
            gridColumn: 1,
            alignSelf: 'flex-start',
            display: 'flex',
            alignItems: 'center',
            height: `${labelSizeToLineHeight[size]}px`,
          }}
        >
          {control}
        </Box>
        <LabelWithDescription
          {...labelWithDescriptionProps}
          label={label}
          size={size}
          labelWeight={labelWeight}
          required={required}
          infoTooltip={infoTooltip}
          description={description}
          disabled={disabledProp}
          slotProps={{
            label: { ...slotProps?.label, htmlFor: controlId },
            infoTooltip: slotProps?.infoTooltip,
            descriptionWrapper: slotProps?.descriptionWrapper,
            errorMessageWrapper: slotProps?.errorMessageWrapper,
            bottomContentWrapper: slotProps?.bottomContentWrapper,
          }}
          error={hasErrorMessage ? errorProp : undefined}
          bottomContent={bottomContent}
          sx={{ gridColumn: 3, minWidth: 0 }}
        />
      </Box>
    </ControlLabelContext.Provider>
  );
};

ControlLabel.displayName = 'ControlLabel';
